import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { DeleteOutlined, EditOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import { formatInventoryLocation } from "../../../../utils"
import type { TransferFormPallet } from "../../types"
import { formatPalletFormDestination, getLotRowBorderClassName } from "../../utils/transfer"

import type { WarehouseArea } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

const PALLET_LOCATION_COLUMN_COUNT = 2
const PALLET_DESTINATION_COLUMN_COUNT = 2

interface TransferFormPalletTableProps {
  pallets: TransferFormPallet[]
  uomName: string
  targetWarehouseArea?: WarehouseArea
  readOnly?: boolean
  onPalletOverride: (palletReferenceNumber: number) => void
  onPalletRemove: (palletReferenceNumber: number) => void
  onLotRemove: (rawInventoryId: string) => void
}

export const TransferFormPalletTable: FC<TransferFormPalletTableProps> = ({
  pallets,
  uomName,
  targetWarehouseArea,
  readOnly = false,
  onPalletOverride,
  onPalletRemove,
  onLotRemove,
}) => (
  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Reference #</TableCell>
          <TableCell>Current Location</TableCell>
          <TableCell>Lot #</TableCell>
          <TableCell>Batch #</TableCell>
          <TableCell>Qty to Move</TableCell>
          {targetWarehouseArea ? (
            <>
              <TableCell>Destination</TableCell>
              <TableCell>LPN</TableCell>
            </>
          ) : (
            <TableCell>New Location</TableCell>
          )}
          <TableCell className='w-20' />
        </TableRow>
      </TableHead>
      <TableBody>
        {pallets.map((pallet) => {
          const destinationCells = targetWarehouseArea ? (
            <>
              <TableCell>{formatWarehouseLabel(targetWarehouseArea)}</TableCell>
              <TableCell>{pallet.lpn ?? ""}</TableCell>
            </>
          ) : (
            <>
              <TableCell>
                <div className='my-[-8px] flex items-center gap-x-2'>
                  {formatPalletFormDestination(pallet.destination)}
                  <IconButton
                    size='small'
                    disabled={readOnly}
                    onClick={() => onPalletOverride(pallet.referenceNbr)}
                    data-testid={`transfer.form-product-pallet-table.override-location-button-${pallet.referenceNbr}`}
                  >
                    <EditOutlined />
                  </IconButton>
                </div>
              </TableCell>
              <TableCell>
                <div className='my-[-8px] flex items-center justify-end'>
                  <IconButton
                    size='small'
                    disabled={readOnly}
                    onClick={() => onPalletRemove(pallet.referenceNbr)}
                    data-testid={`transfer.form-product-pallet-table.remove-pallet-button-${pallet.referenceNbr}`}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </div>
              </TableCell>
            </>
          )

          return pallet.lots.map((lot, lotIndex) => {
            const isFirstLot = lotIndex === 0
            const isLastLot = lotIndex === pallet.lots.length - 1

            return (
              <TableRow
                key={lot.rawInventoryId}
                className={twMerge("align-top", getLotRowBorderClassName(isLastLot))}
                data-testid={`transfer.form-product-pallet-table.row-${lot.rawInventoryId}`}
              >
                {isFirstLot ? (
                  <>
                    <TableCell>{pallet.referenceNbr}</TableCell>
                    <TableCell>{formatInventoryLocation(pallet.location)}</TableCell>
                  </>
                ) : (
                  <TableCell colSpan={PALLET_LOCATION_COLUMN_COUNT} />
                )}
                <TableCell>{lot.lotNbr ?? ""}</TableCell>
                <TableCell>{lot.batchNbr ?? ""}</TableCell>
                <TableCell className='whitespace-nowrap'>{`${formatNumber(lot.qtyToMove)} ${uomName}`}</TableCell>
                {isFirstLot ? destinationCells : <TableCell colSpan={PALLET_DESTINATION_COLUMN_COUNT} />}
                {targetWarehouseArea && (
                  <TableCell>
                    <div className='my-[-8px] flex items-center justify-end'>
                      <IconButton
                        size='small'
                        disabled={readOnly}
                        onClick={() => onLotRemove(lot.rawInventoryId)}
                        data-testid={`transfer.form-product-pallet-table.remove-lot-button-${lot.rawInventoryId}`}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )
          })
        })}
      </TableBody>
    </Table>
  </TableContainer>
)
